import React, { useState, useEffect, useRef } from "react";
import { Form } from "react-bootstrap";
import uuid from "react-uuid";
import usePaginationValuesWhere from "../../hooks/usePaginationValuesWhere";
import useExceptionHandler from "hooks/useExceptionHandler";
import useMessage from "hooks/useMessage";
import { RequestLogManager } from "managers";
import { GridView, SvgComponent, Simplebar, InputSidebarContainer } from "components";
import ListHeader from "components/PantallaList/Header/ListHeader";
import IconButton from "components/IconButton/IconButton";
import ToolTip from "components/ToolTip/ToolTip";
import Exportar from "components/Exportar/Exportar";
import { DateTimePicker } from "components/DateTimePicker/DateTimePicker";
import { DateHelper } from "utils";
import { RequestLog } from "models/ECF/RequestLog";

export default function RequestLogList({ showLoading, hideLoading }) {
  const [where, setWhere] = useState({});
  const [pagination, setPagination] = useState({
    EsPaginable: true,
    CantidadPorPagina: 15,
    EsOrdernable: true,
    OrderBy: "Id",
  });
  const [list, setList] = useState([]);
  const [currentLog, setCurrentLog] = useState({});
  const [showDetalle, setShowDetalle] = useState(false);
  const [showExportar, setShowExportar] = useState(false);
  const [fechaFilterKey, setFechaFilterKey] = useState(uuid());
  const ref = useRef(null);
  const { setPaginationValuesWhere } = usePaginationValuesWhere();
  const { handleException } = useExceptionHandler();
  const { cerrarAlertas } = useMessage();

  useEffect(() => {
    buscar();
    return () => {
      cerrarAlertas();
      hideLoading();
    };
  }, [where]);

  const buscar = () => {
    const whereWithPaginationValues = {
      ...where,
      ...pagination,
      FechaInicio: where.RangoFechas?.fechaInicio,
      FechaFin: where.RangoFechas?.fechaFin,
    };

    RequestLogManager.listado(whereWithPaginationValues)
      .then((res) => {
        setPaginationValuesWhere(res.Headers, pagination, setPagination);

        try {
          for (let log of res.Data) {
            log["FechaCreacion"] = DateHelper.formatStringDate(
              log["FechaCreacion"],
              true
            );
          }
        } catch (err) { }

        setList(res.Data);
      })
      .catch((error) => {
        handleException(error);
      })
      .finally(() => {
        hideLoading();
      });
  };

  const onPageIndexChanging = (pageNumber) => {
    setPagination((prevstate) => {
      return { ...prevstate, IndiceDePagina: pageNumber - 1 };
    });
    setWhere({ ...where });
  };

  const onReset = () => {
    setFechaFilterKey(uuid());
  };

  const handleVerClick = (id) => {
    showLoading();
    RequestLogManager.buscarPorId(id)
      .then((data) => {
        setCurrentLog(data);
        setShowDetalle(true);
      })
      .catch((error) => {
        handleException(error);
      })
      .finally(() => {
        hideLoading();
      });
  };

  return (
    <div>
      <Simplebar
        ref={ref}
        backdrop={true}
        placement={"end"}
        portal={false}
        visible={showDetalle}
        setShow={setShowDetalle}
        useLoading={true}
        title="Detalle Request Log"
        size="xl"
        btnLeft={{ show: false }}
        btnRight={{ show: false }}
      >
        <InputSidebarContainer label="Dirección IP">
          <Form.Control disabled value={currentLog.DireccionIP ?? ""} />
        </InputSidebarContainer>
        <InputSidebarContainer label="Contenido">
          <Form.Control
            as="textarea"
            rows={8}
            style={{ resize: "vertical" }}
            disabled
            value={currentLog.Contenido ?? ""}
          />
        </InputSidebarContainer>
        <InputSidebarContainer label="Respuesta">
          <Form.Control
            as="textarea"
            rows={8}
            style={{ resize: "vertical" }}
            disabled
            value={currentLog.Respuesta ?? ""}
          />
        </InputSidebarContainer>
      </Simplebar>

      <Exportar
        PROPIEDADES_EXPORTAR={RequestLog.PROPIEDADES_EXPORTAR}
        showExportar={showExportar}
        setShowExportar={setShowExportar}
        showLoading={showLoading}
        hideLoading={hideLoading}
        where={where}
        exportarCallBack={RequestLogManager.exportarExcel}
      ></Exportar>

      <div style={{ paddingRight: "6px" }}>
        <ListHeader
          titulo={"Request Log"}
          exportar={() => setShowExportar(true)}
        />
      </div>
      <GridView
        pagination={pagination}
        setPagination={setPagination}
        allowPaging={pagination.EsPaginable}
        pageSize={pagination.CantidadPorPagina}
        pageIndex={pagination.IndiceDePagina}
        lastPageNumber={pagination.PaginasTotal}
        dataSource={list}
        onPageIndexChanging={onPageIndexChanging}
        where={where}
        setWhere={setWhere}
        onReset={onReset}
        height="70vh"
      >
        <GridView.Column
          width="100px"
          bodyRenderer={(row) => (
            <div className="float-right">
              <IconButton onClick={() => handleVerClick(row.Id)}>
                <ToolTip message="Ver detalle">
                  <div>
                    <SvgComponent iconName="view" height="25px" width="25px" />
                  </div>
                </ToolTip>
              </IconButton>
            </div>
          )}
        />
        <GridView.Column
          headerText="Id"
          dataField="Id"
          sortExpression="Id"
        />
        <GridView.Column
          headerText="Compañía Id"
          dataField="CompaniaId"
          sortExpression="CompaniaId"
        />
        <GridView.Column
          headerText="Transacción Id"
          dataField="TransaccionId"
          sortExpression="TransaccionId"
        />
        <GridView.Column
          date
          hideFilter
          headerText="Fecha Creación"
          dataField="FechaCreacion"
          sortExpression="FechaCreacion"
          width="230px"
          filterRenderer={(onInputChange) => {
            return (
              <DateTimePicker
                key={fechaFilterKey}
                soloFecha
                esRangoFechas
                esGrid
                usarLabel={false}
                fechaInicioOriginal={where?.RangoFechas?.fechaInicio || ""}
                fechaFinOriginal={where?.RangoFechas?.fechaFin || ""}
                onChange={(e) => {
                  const updatedRange = { ...e };

                  if (updatedRange.fechaInicio) {
                    const start = new Date(updatedRange.fechaInicio);
                    start.setHours(0, 0, 0, 0);
                    updatedRange.fechaInicio = start;
                  }

                  if (updatedRange.fechaFin) {
                    const end = new Date(updatedRange.fechaFin);
                    end.setHours(23, 59, 59, 0);
                    updatedRange.fechaFin = end;
                  }

                  onInputChange({
                    target: { name: "RangoFechas", value: updatedRange }
                  })
                }}
              />
            );
          }}
        />
        <GridView.Column
          headerText="Método"
          dataField="Method"
          sortExpression="Method"
        />
        <GridView.Column
          width="300px"
          headerText="Endpoint"
          dataField="EndPoint"
          sortExpression="EndPoint"
        />
      </GridView>
    </div>
  );
}
